import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Customer } from '../customer/customerType';
import { InvoiceProjectCustomer } from '../invoice/invoiceType';
import CustomerPaymentApi from './customerPaymentApi';
import CustomerApi from '../customer/customerApi';
import { InvoiceWithPayments, ProjectWithInvoices } from './customerPaymentType';
import { formatAddress, formatTimestamp } from '../lib/global_function';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Select } from '../components/ui/select';
import { Label } from '../components/ui/label';
import Spinner from '../components/ui/spinner';
import { Trash, FilePlus, Pencil, Mail, ChevronDown, ChevronUp, CheckCircle, AlertCircle, Info, Undo2 } from 'lucide-react';

const constructCustomerFromInvoice = (invoice: InvoiceProjectCustomer): Customer => ({
  customer_id: invoice.customer_id,
  first_name: invoice.customer_first_name,
  last_name: invoice.customer_last_name,
  address_1: invoice.customer_address_1,
  address_2: invoice.customer_address_2,
  city: invoice.customer_city,
  state: invoice.customer_state,
  zip: invoice.customer_zip,
  phone: invoice.customer_phone,
  email: invoice.customer_email,
});

interface CustomerPaymentPageProps { }

const CustomerPaymentPage: React.FC<CustomerPaymentPageProps> = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const customer: Customer | undefined = state?.customer;
  const invoice: InvoiceProjectCustomer | undefined = state?.invoice;
  const isInvoiceView = !!invoice;

  const [projects, setProjects] = useState<ProjectWithInvoices[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [filterStatus, setFilterStatus] = useState<'all' | 'outstanding' | 'paid'>('all');
  const [filterProject, setFilterProject] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest' | 'balance'>('newest');

  // New states for customer selection
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | undefined>(customer);

  const breadcrumbs = isInvoiceView
    ? [
      { label: 'Project', path: '/main/project' },
      { label: 'Invoice', path: '/main/project/invoice' },
      { label: 'Customer Payment', path: '/main/project/invoice/customerpayment' },
    ]
    : [
      { label: 'Customer', path: '/main/customer' },
      { label: 'Customer Payment', path: '/main/customer/customerpayment' },
    ];

  // Fetch customers if no customer is passed
  useEffect(() => {
    const fetchCustomers = async () => {
      if (!customer && !invoice) {
        setLoading(true);
        try {
          const fetchedCustomers = await CustomerApi.get();
          setCustomers(fetchedCustomers);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to load customers.');
        } finally {
          setLoading(false);
        }
      }
    };
    fetchCustomers();
  }, [customer, invoice]);

  // Set initial customer ID if customer or invoice is passed
  useEffect(() => {
    if (customer?.customer_id) {
      setSelectedCustomerId(customer.customer_id);
      setSelectedCustomer(customer);
    } else if (invoice?.customer_id) {
      setSelectedCustomerId(invoice.customer_id);
      setSelectedCustomer(constructCustomerFromInvoice(invoice));
    }
  }, [customer, invoice]);

  // Fetch payment data when customer is selected or changed
  useEffect(() => {
    setError(null);

    const fetchPaymentData = async () => {
      if (!selectedCustomerId) {
        setProjects([]);
        return;
      }

      // Reset filters to 'all' BEFORE fetching data
      setFilterStatus('all');
      setFilterProject('all');

      setLoading(true);

      try {
        // Fetch ALL data for the selected customer
        const { projects: fetchedProjects } = await CustomerPaymentApi.getCustomerPaymentByCustomerId(selectedCustomerId);

        // Set all projects data - no filtering applied at fetch time
        setProjects(fetchedProjects);

        // Expand all projects by default
        setExpandedProjects(new Set(fetchedProjects.map(p => p.project_id)));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load payments.');
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentData();
  }, [selectedCustomerId]);

  useEffect(() => {
    setError(null);
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.menu-button') && !target.closest('.menu-dropdown')) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleCustomerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const customerId = e.target.value;
    setSelectedCustomerId(customerId);
    setError(null);
    const selected = customers.find(c => String(c.customer_id) === String(customerId));
    setSelectedCustomer(selected);
  };

  const formatCustomerAddress = () => {
    if (selectedCustomer) {
      return formatAddress(
        selectedCustomer.address_1,
        selectedCustomer.address_2,
        selectedCustomer.city,
        selectedCustomer.state,
        selectedCustomer.zip
      );
    }
    return '';
  };

  // First level filtering for invoice view
  const filteredProjects = isInvoiceView && invoice
    ? projects
      .map((project) => ({
        ...project,
        invoices: project.invoices.filter((inv) => inv.invoice_id === invoice.invoice_id),
      }))
      .filter((project) => project.invoices.length > 0)
    : projects;

  // Apply user-selected filters and sorting
  const processedProjects = filteredProjects
    .map(project => ({
      ...project,
      invoices: project.invoices.filter(inv => {
        const balance = (inv.final_total || 0) - (inv.amount_paid || 0);
        if (filterStatus === 'outstanding') return balance > 0;
        if (filterStatus === 'paid') return balance <= 0;
        return true;
      })
    }))
    .filter(project => {
      if (filterProject === 'all') return project.invoices.length > 0;
      return String(project.project_id) === String(filterProject) && project.invoices.length > 0;
    })
    .map(project => ({
      ...project,
      invoices: [...project.invoices].sort((a, b) => {
        if (sortOrder === 'newest') {
          return new Date(b.issue_date).getTime() - new Date(a.issue_date).getTime();
        }
        if (sortOrder === 'oldest') {
          return new Date(a.issue_date).getTime() - new Date(b.issue_date).getTime();
        }
        if (sortOrder === 'balance') {
          const balanceA = (a.final_total || 0) - (a.amount_paid || 0);
          const balanceB = (b.final_total || 0) - (b.amount_paid || 0);
          return balanceB - balanceA;
        }
        return 0;
      })
    }));

  const handleNavigate = (path: string, data: any) => {
    navigate(path, { state: data });
  };

  const toggleProject = (projectId: string) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  const handleBackToCustomer = () => {
    setError(null);
    navigate('/main/customer');
  };

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
          <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
            Customer Payment History
          </h1>
        </div>

        {/* Error Message - Only show when not loading */}
        {error && !loading && (
          <div className="flex items-center gap-2">
            <Alert variant="error" className="flex-1">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">{error}</div>
                <Button size="medium" variant="default" onClick={handleBackToCustomer}>
                  <Undo2 size={18} />
                  Back To Customer
                </Button>
              </div>
            </Alert>
          </div>
        )}

        {/* Customer Selector - Show only when no customer passed */}
        {!customer && !invoice && customers.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Select Customer</h3>
            <div className="max-w-3xl">
              <Label htmlFor="customer-select" className="mb-2">
                Customer <span className="text-red-500">*</span>
              </Label>
              <Select
                id="customer-select"
                value={selectedCustomerId}
                onChange={handleCustomerChange}
              >
                <option value="">Select a Customer</option>
                {customers.map((cust) => (
                  <option key={cust.customer_id} value={cust.customer_id}>
                    {cust.first_name} {cust.last_name} - {formatAddress(
                      cust.address_1,
                      cust.address_2,
                      cust.city,
                      cust.state,
                      cust.zip
                    )}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        )}

        {/* Selected Customer Info - Always show when customer is selected */}
        {selectedCustomer && selectedCustomerId && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-foreground">
              <span className="font-semibold">Customer:</span>{' '}
              {selectedCustomer.first_name} {selectedCustomer.last_name}
              {', '}
              <span className="font-semibold">Address:</span> {formatCustomerAddress()}
            </p>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <Spinner />
        ) : (
          <>
            {/* Show message when no customer is selected */}
            {!selectedCustomerId && !customer && !invoice && (
              <div className="text-center py-12">
                <p className="text-muted text-base">Please select a customer to view payment history.</p>
              </div>
            )}

            {/* Show content when customer is selected */}
            {selectedCustomerId && (
              <>
                {filteredProjects.length > 0 && <PaymentSummary projects={filteredProjects} />}

                {/* Filters */}
                {!isInvoiceView && filteredProjects.length > 0 && (
                  <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4">
                    <h3 className="text-base font-semibold text-foreground mb-3">Filter Invoices</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="filter-status" className="mb-1">Status</Label>
                        <Select
                          id="filter-status"
                          value={filterStatus}
                          onChange={(e) => setFilterStatus(e.target.value as 'all' | 'outstanding' | 'paid')}
                        >
                          <option value="all">All Invoices</option>
                          <option value="outstanding">Outstanding Only</option>
                          <option value="paid">Paid Only</option>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="filter-project" className="mb-1">Project</Label>
                        <Select
                          id="filter-project"
                          value={filterProject}
                          onChange={(e) => setFilterProject(e.target.value)}
                        >
                          <option value="all">All Projects</option>
                          {filteredProjects.map((p) => (
                            <option key={p.project_id} value={p.project_id}>
                              {p.name || `Project #${p.project_id}`}
                            </option>
                          ))}
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                {!processedProjects.length && !error ? (
                  <Alert variant="info">
                    No payments found using search filters.
                  </Alert>
                ) : (
                  <div className="space-y-4">
                    {processedProjects.map((project) => (
                      <ProjectSection
                        key={project.project_id}
                        project={project}
                        isInvoiceView={isInvoiceView}
                        invoice={invoice}
                        customer={selectedCustomer}
                        handleNavigate={handleNavigate}
                        openMenuId={openMenuId}
                        setOpenMenuId={setOpenMenuId}
                        isExpanded={expandedProjects.has(project.project_id)}
                        onToggle={() => toggleProject(project.project_id)}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

interface ProjectSectionProps {
  project: ProjectWithInvoices;
  isInvoiceView: boolean;
  invoice?: InvoiceProjectCustomer;
  customer?: Customer;
  handleNavigate: (path: string, data: any) => void;
  openMenuId: string | null;
  setOpenMenuId: (id: string | null) => void;
  isExpanded: boolean;
  onToggle: () => void;
}

const ProjectSection: React.FC<ProjectSectionProps> = ({
  project,
  isInvoiceView,
  invoice,
  customer,
  handleNavigate,
  openMenuId,
  setOpenMenuId,
  isExpanded,
  onToggle
}) => {
  const totalBalance = project.invoices.reduce((sum, inv) =>
    sum + ((inv.final_total || 0) - (inv.amount_paid || 0)), 0
  );

  const hasProjectDetails = project.project_description || project.project_type;

  return (
    <div className="bg-white border-2 border-blue-400 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full p-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 hover:bg-gray-50 transition-colors group"
      >
        <div className="text-left flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-foreground text-base sm:text-lg">
              {project.name || `Project #${project.project_id}`}
            </h3>
            {hasProjectDetails && (
              <div className="relative group/tooltip">
                <Info className="text-gray-400 hover:text-primary cursor-help" size={16} />
                <div className="invisible group-hover/tooltip:visible absolute left-0 top-6 z-50 w-80 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg">
                  {project.project_description && (
                    <div className="mb-2">
                      <span className="font-semibold">Description:</span>
                      <p className="mt-1">{project.project_description}</p>
                    </div>
                  )}
                  {project.project_type && (
                    <div>
                      <span className="font-semibold">Type:</span> {project.project_type}
                    </div>
                  )}
                  <div className="absolute left-3 -top-1 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-3 text-sm text-gray-600 mt-1">
            <span>{project.invoices.length} {project.invoices.length === 1 ? 'Invoice' : 'Invoices'}</span>
            <span>•</span>
            <span className={`font-semibold ${totalBalance > 0 ? 'text-red-600' : 'text-green-600'}`}>
              Balance: ${totalBalance.toFixed(2)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-center">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${project.is_active
            ? 'bg-green-100 text-green-800'
            : 'bg-gray-100 text-gray-600'
            }`}>
            {project.is_active ? 'ACTIVE' : 'INACTIVE'}
          </span>
          {isExpanded ? (
            <ChevronUp className="text-gray-400" size={20} />
          ) : (
            <ChevronDown className="text-gray-400" size={20} />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          {project.invoices.length === 0 ? (
            <Alert variant="info">No invoices and payments found.</Alert>
          ) : (
            <div className="space-y-4">
              {project.invoices.map((inv) => (
                <InvoiceSection
                  key={inv.invoice_id}
                  project={project}
                  invoice={inv}
                  isInvoiceView={isInvoiceView}
                  providedInvoice={invoice}
                  customer={customer}
                  handleNavigate={handleNavigate}
                  openMenuId={openMenuId}
                  setOpenMenuId={setOpenMenuId}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface InvoiceSectionProps {
  project: ProjectWithInvoices;
  invoice: InvoiceWithPayments;
  isInvoiceView: boolean;
  providedInvoice?: InvoiceProjectCustomer;
  customer?: Customer;
  handleNavigate: (path: string, data: any) => void;
  openMenuId: string | null;
  setOpenMenuId: (id: string | null) => void;
}

const InvoiceSection: React.FC<InvoiceSectionProps> = ({
  project,
  invoice,
  isInvoiceView,
  providedInvoice,
  customer,
  handleNavigate,
  openMenuId,
  setOpenMenuId,
}) => {
  const balanceDue = (invoice.final_total || 0) - (invoice.amount_paid || 0);
  const isBalanceZero = balanceDue <= 0;

  // Ensure customer is always available for navigation
  const customerForNavigation = customer || 
    (providedInvoice ? constructCustomerFromInvoice(providedInvoice) : undefined);

  // Create a complete invoice object with customer and project data
  const invoiceWithCustomerData = providedInvoice || (customerForNavigation ? {
    invoice_id: String(invoice.invoice_id),
    name: invoice.name,
    payment_instruction: '',
    tax_rate: invoice.tax_rate,
    discount_usd: invoice.discount_usd,
    amount_paid: invoice.amount_paid,
    issue_date: invoice.issue_date,
    due_date: invoice.due_date,
    sub_total_sum_lineitems: invoice.sub_total_sum_lineitems,
    sub_total_after_tax: 0,
    tax_amount: invoice.tax_amount,
    final_total: invoice.final_total,
    customer_id: String(customerForNavigation.customer_id),
    customer_first_name: customerForNavigation.first_name,
    customer_last_name: customerForNavigation.last_name,
    customer_address_1: customerForNavigation.address_1,
    customer_address_2: customerForNavigation.address_2,
    customer_city: customerForNavigation.city,
    customer_state: customerForNavigation.state,
    customer_zip: customerForNavigation.zip,
    customer_phone: customerForNavigation.phone,
    customer_email: customerForNavigation.email,
    project_id: String(project.project_id),
    project_name: project.name || '',
    project_description: project.project_description || '',
    project_type: project.project_type || '',
    project_start_date: '',
    project_end_date: '',
  } : undefined);

  return (
    <div className="bg-white rounded-lg border shadow-sm">
      <div className="p-4">
        {/* Invoice Header with Status Badge and Buttons */}
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-3 mb-4">
          {/* Left: Invoice Info */}
          <div className="flex-shrink-0">
            <h4 className="font-semibold text-foreground text-base">
              Invoice #{invoice.invoice_id}: {invoice.name || `Invoice #${invoice.invoice_id}`}
            </h4>
            <p className="text-sm text-gray-600 mt-1">
              Issued: {formatTimestamp(invoice.issue_date)} • Due: {formatTimestamp(invoice.due_date)}
            </p>
          </div>

          {/* Status Badge and Buttons */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-3 flex-shrink-0 sm:flex-1">
            {/* Status Badge */}
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border-l-4 ${isBalanceZero
              ? 'bg-green-50 border-green-500'
              : 'bg-red-50 border-red-500'
              }`}>
              {isBalanceZero ? (
                <CheckCircle className="text-green-600" size={20} />
              ) : (
                <AlertCircle className="text-red-600" size={20} />
              )}
              <span className={`font-semibold text-sm whitespace-nowrap ${isBalanceZero ? 'text-green-800' : 'text-red-800'
                }`}>
                {isBalanceZero ? 'PAID IN FULL' : 'OUTSTANDING'}
              </span>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              <Button
                size="medium"
                variant="default"
                onClick={() => {
                  if (invoiceWithCustomerData) {
                    handleNavigate('/main/customerpayment/create', {
                      invoice: invoiceWithCustomerData,
                    });
                  }
                }}
                disabled={isBalanceZero || !invoiceWithCustomerData}
              >
                <FilePlus size={16} /> Add Payment
              </Button>
              <Button
                size="medium"
                variant="default"
                onClick={() => {
                  handleNavigate('/main/customerpayment/sendstatement', {
                    project,
                    invoice,
                    customer: customerForNavigation
                  });
                }}
              >
                <Mail size={16} /> Send Statement
              </Button>
            </div>
          </div>
        </div>

        {/* Invoice Financial Summary - Responsive */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-3 text-sm">
          <div>
            <span className="text-gray-600">Subtotal:</span>{' '}
            <span className="font-semibold">${Number(invoice.sub_total_sum_lineitems || 0).toFixed(2)}</span>
          </div>
          <div>
            <span className="text-gray-600">Tax ({Number(invoice.tax_rate || 0).toFixed(2)}%):</span>{' '}
            <span className="font-semibold">${Number(invoice.tax_amount || 0).toFixed(2)}</span>
          </div>
          <div>
            <span className="text-gray-600">Discount:</span>{' '}
            <span className="font-semibold text-green-600">-${Number(invoice.discount_usd || 0).toFixed(2)}</span>
          </div>
          <div>
            <span className="text-gray-600">Final Total:</span>{' '}
            <span className="font-semibold">${Number(invoice.final_total || 0).toFixed(2)}</span>
          </div>
          <div>
            <span className="text-gray-600">Paid:</span>{' '}
            <span className="font-semibold text-green-600">${Number(invoice.amount_paid || 0).toFixed(2)}</span>
          </div>
          <div>
            <span className="text-gray-600">Amount Due:</span>{' '}
            <span className={`font-semibold ${balanceDue > 0 ? 'text-red-600' : 'text-green-600'}`}>
              ${Number(balanceDue).toFixed(2)}
            </span>
          </div>
        </div>

        {/* Additional Details - Only show in invoice view if needed */}
        {isInvoiceView && providedInvoice && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3 text-sm">
            <div>
              <span className="text-gray-600">Subtotal:</span>{' '}
              <span className="font-semibold">${Number(providedInvoice.sub_total_sum_lineitems).toFixed(2)}</span>
            </div>
            <div>
              <span className="text-gray-600">Tax Rate:</span>{' '}
              <span className="font-semibold">{Number(providedInvoice.tax_rate).toFixed(2)}%</span>
            </div>
            <div>
              <span className="text-gray-600">Tax Amount:</span>{' '}
              <span className="font-semibold">${Number(providedInvoice.tax_amount).toFixed(2)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Payment History - Desktop Table */}
      {invoice.customer_payments?.length ? (
        <>
          <div className="border-t hidden md:block">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100 border-b">
                    <th className="px-4 py-3 text-left font-semibold text-foreground">#</th>
                    <th className="px-4 py-3 text-left font-semibold text-foreground">Amount</th>
                    <th className="px-4 py-3 text-left font-semibold text-foreground">Date</th>
                    <th className="px-4 py-3 text-left font-semibold text-foreground">Method</th>
                    <th className="px-4 py-3 text-left font-semibold text-foreground">Note</th>
                    <th className="px-4 py-3 text-left font-semibold text-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {invoice.customer_payments.map((payment, index) => (
                    <tr key={payment.customer_payment_id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">{index + 1}</td>
                      <td className="px-4 py-3 font-bold text-green-600">
                        ${Number(payment.amount).toFixed(2)}
                      </td>
                      <td className="px-4 py-3">{formatTimestamp(payment.payment_date)}</td>
                      <td className="px-4 py-3">{payment.payment_method}</td>
                      <td className="px-4 py-3 text-gray-600">{payment.note || '-'}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            className="p-2 hover:bg-gray-200 rounded transition-colors"
                            onClick={() => handleNavigate('/main/customerpayment/update', {
                              project,
                              invoice,
                              customer_payment: payment,
                              customer: customerForNavigation
                            })}
                            title="Edit payment"
                          >
                            <Pencil size={16} className="text-primary" />
                          </button>
                          <button
                            className="p-2 hover:bg-red-100 rounded transition-colors"
                            onClick={() => handleNavigate('/main/customerpayment/delete', {
                              project,
                              invoice,
                              customer_payment: payment,
                              customer: customerForNavigation
                            })}
                            title="Delete payment"
                          >
                            <Trash size={16} className="text-primary" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-green-50 border-t-2 border-gray-300">
                    <td className="px-4 py-3 font-bold">Total:</td>
                    <td className="px-4 py-3 font-bold text-green-600">
                      ${invoice.customer_payments.reduce((sum, payment) => sum + Number(payment.amount), 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3"></td>
                    <td className="px-4 py-3"></td>
                    <td className="px-4 py-3"></td>
                    <td className="px-4 py-3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Payment History - Mobile Cards */}
          <div className="border-t md:hidden p-4 space-y-3 bg-gray-50">
            {invoice.customer_payments.map((payment, index) => (
              <div key={payment.customer_payment_id} className="bg-white border rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <span className="font-bold text-green-600 text-lg">${Number(payment.amount).toFixed(2)}</span>
                  <span className="text-xs text-gray-500">Payment #{index + 1}</span>
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date:</span>
                    <span className="font-medium">{formatTimestamp(payment.payment_date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Method:</span>
                    <span className="font-medium">{payment.payment_method}</span>
                  </div>
                  {payment.note && (
                    <div className="pt-2 border-t">
                      <span className="text-gray-600 text-xs">Note:</span>
                      <p className="text-xs text-gray-700 mt-1">{payment.note}</p>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 mt-3">
                  <Button
                    size="medium"
                    variant="inverted"
                    onClick={() => handleNavigate('/main/customerpayment/update', {
                      project,
                      invoice,
                      customer_payment: payment,
                      customer: customerForNavigation
                    })}
                    className="flex-1"
                  >
                    <Pencil size={14} /> Edit
                  </Button>
                  <Button
                    size="medium"
                    variant="default"
                    onClick={() => handleNavigate('/main/customerpayment/delete', {
                      project,
                      invoice,
                      customer_payment: payment,
                      customer: customerForNavigation
                    })}
                    className="flex-1 bg-red-600 hover:bg-red-700"
                  >
                    <Trash size={14} /> Delete
                  </Button>
                </div>
              </div>
            ))}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-3">
              <div className="flex justify-between items-center">
                <span className="font-bold text-foreground">Total Payments:</span>
                <span className="font-bold text-green-600 text-lg">
                  ${invoice.customer_payments.reduce((sum, payment) => sum + Number(payment.amount), 0).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="border-t p-4 text-center text-muted italic bg-gray-50">
          <p className="text-sm">No payments received for this invoice.</p>
        </div>
      )}
    </div>
  );
};

interface PaymentSummaryProps {
  projects: ProjectWithInvoices[];
}

const PaymentSummary: React.FC<PaymentSummaryProps> = ({ projects }) => {
  const invoices = projects.flatMap((p) => p.invoices);
  const totalDue = invoices.reduce((sum, inv) => sum + Number(inv.final_total), 0);
  const totalPaid = invoices.reduce((sum, inv) => sum + Number(inv.amount_paid), 0);
  const totalPayments = invoices.reduce((sum, inv) => sum + (inv.customer_payments?.length || 0), 0);
  const balance = totalDue - totalPaid;

  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold text-foreground mb-4">Payment Summary</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="text-xs text-gray-600 mb-1">Total Due</div>
          <div className="text-lg font-bold text-foreground">${totalDue.toFixed(2)}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="text-xs text-gray-600 mb-1">Total Paid</div>
          <div className="text-lg font-bold text-green-600">${totalPaid.toFixed(2)}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="text-xs text-gray-600 mb-1">Balance</div>
          <div className={`text-lg font-bold ${balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
            ${balance.toFixed(2)}
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="text-xs text-gray-600 mb-1">Payments Received</div>
          <div className="text-lg font-bold text-foreground">{totalPayments}</div>
        </div>
      </div>
    </div>
  );
};

export default CustomerPaymentPage;